import type { OtpBlock } from '@blogforge/shared';
import type { DragPath } from '../../types';
import { useEditorStore } from '../../stores/editorStore';
import VariableInfusedTextEditor, {
  type RichTextEditorHandle,
} from '../tiptap/VariableInfusedTextEditor';
import { useRef } from 'react';

interface OtpInspectorProps {
  block: OtpBlock;
  path: DragPath;
  variableNameOptions?: string[];
}

// Mirrors the schema bounds. Blocks are never zod-parsed on the way in
// (same story as ImageInspector), so every attr below is treated as
// optional and the defaults here are the runtime truth.
const MIN_LENGTH = 4;
const MAX_LENGTH = 8;
const MIN_GROUP = 0;
const MAX_GROUP = 4;

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, Math.round(n)));

export function OtpInspector({ block, path, variableNameOptions = [] }: OtpInspectorProps) {
  const richTextRef = useRef<RichTextEditorHandle>(null);
  const updateBlock = useEditorStore((s) => s.updateBlock);
  const attrs = block.attrs;

  const set = (patch: Partial<OtpBlock['attrs']>) => {
    updateBlock(block.id, { attrs: { ...attrs, ...patch } }, path);
  };

  // Rendered values — same `?? default` chain the canvas uses, so the
  // number inputs are always controlled and never appear blank.
  const length = attrs?.length ?? 6;
  const groupSize = attrs?.groupSize ?? 3;

  return (
    <div className="space-y-4">
      <div>
        <label className="field-label">Code source</label>
        <VariableInfusedTextEditor
          value={attrs?.code ?? ''}
          onChange={(val, codeIsMustache) => set({ code: val, codeIsMustache })}
          placeholder="e.g. {{otp}} — use @ to reference a variable"
          options={variableNameOptions}
          ref={richTextRef}
          tagsOnly={true}
          maxTags={1}
        />
        <p className="text-muted mt-1 text-xs">
          Resolved at send time. The canvas shows mock digits when this looks like a token.
        </p>
      </div>

      <div>
        <label className="field-label" htmlFor="otp-content">
          Heading
        </label>
        <input
          id="otp-content"
          type="text"
          className="field-input"
          placeholder="Optional heading above the code"
          value={block.content ?? ''}
          onChange={(e) => updateBlock(block.id, { content: e.target.value }, path)}
        />
      </div>

      <div>
        <label className="field-label" htmlFor="otp-label">
          Label
        </label>
        <input
          id="otp-label"
          type="text"
          className="field-input"
          placeholder="Your verification code"
          value={attrs?.label ?? ''}
          onChange={(e) => set({ label: e.target.value })}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="otp-length">
            Digits
          </label>
          <input
            id="otp-length"
            type="number"
            min={MIN_LENGTH}
            max={MAX_LENGTH}
            className="field-input"
            value={length}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (!Number.isFinite(n)) return;
              set({ length: clamp(n, MIN_LENGTH, MAX_LENGTH) });
            }}
          />
          <p className="text-muted mt-1 text-xs">
            Boxes shrink above 6 digits so an 8-digit code still fits a 320px viewport.
          </p>
        </div>

        <div>
          <label className="field-label" htmlFor="otp-group">
            Group every
          </label>
          <input
            id="otp-group"
            type="number"
            min={MIN_GROUP}
            max={MAX_GROUP}
            className="field-input"
            value={groupSize}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (!Number.isFinite(n)) return;
              set({ groupSize: clamp(n, MIN_GROUP, MAX_GROUP) });
            }}
          />
          <p className="text-muted mt-1 text-xs">
            {groupSize === 0 ? '0 = no grouping' : `Inserts a wider gap every ${groupSize} digits.`}
          </p>
        </div>
      </div>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={attrs?.boxed ?? true}
          onChange={(e) => set({ boxed: e.target.checked })}
        />
        Show digits in boxes
      </label>
    </div>
  );
}
